import React from "react";
import { TreeViewProvider } from "./TreeViewContext";
import TreeViewMain from "./Main";

/**
 * Presentational Plant -> WBS -> Documents/Input Data/Estimation/2D
 * Drawings tree, driven by useEmsProjectsTreeData(). Shared by the global
 * AppSidebar's ProjectsTreeSection and the full-page EmsExplorer so both
 * look and behave identically for search/expand/load.
 *
 * Clicking a Plant/WBS/Document node calls `onSelect({kind, plant, wbs?,
 * doc?})` so each consumer decides what happens (navigate away, or show
 * inline) — but PV Input Data/Estimation leaves always confirm then
 * navigate away here, since both consumers want that identical behavior
 * (those are full editable forms living on other pages, and leaving this
 * page loses whatever isn't saved there). A drawing leaf instead navigates
 * straight to BomPage with no confirmation.
 *
 * `collapsible` turns the "Projects" heading into a toggle and starts the
 * whole list hidden, and `focusSelectedPlant` narrows the list to just the
 * picked plant afterwards, with the header breadcrumb (Projects / plant)
 * to step back out - everything inside that plant just expands in place,
 * as it does everywhere else. Both are for the narrow side-panel drawer,
 * where showing every plant up front is more noise than help. The
 * full-page explorer and the compare picker leave them off.
 *
 * `openPvDashboardOnSelect` makes selecting a Pressure Vessel WBS (the vessel
 * itself, not one of its revisions) open that vessel's 5D dashboard. Off by
 * default: the explorer shows its own summary pane for a selected WBS, and the
 * picker only ever selects. See buildPvDashboardHref for the link.
 */
export default function EmsProjectsTreeView({
  data,
  activePlantId,
  onSelect,
  pickerMode = false,
  showLegend = true,
  collapsible = false,
  defaultListOpen,
  focusSelectedPlant = false,
  openPvDashboardOnSelect = false,
}) {
  return (
    <TreeViewProvider
      data={data}
      activePlantId={activePlantId}
      onSelect={onSelect}
      pickerMode={pickerMode}
      showLegend={showLegend}
      collapsible={collapsible}
      defaultListOpen={defaultListOpen}
      focusSelectedPlant={focusSelectedPlant}
      openPvDashboardOnSelect={openPvDashboardOnSelect}
    >
      <TreeViewMain />
    </TreeViewProvider>
  );
}

export { useTreeViewContext } from "./TreeViewContext";
